import { useEffect, useRef, useState } from 'react';
import { Box, Button, type ButtonProps } from '@mui/material';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_CLIENT_ID ?? '';
const PRIMARY_COLOR = '#75428C';

// Google's renderButton only accepts a width between 200px and 400px.
const GIS_MIN_WIDTH = 200;
const GIS_MAX_WIDTH = 400;

function clampGisWidth(width: number): number {
  return Math.max(GIS_MIN_WIDTH, Math.min(GIS_MAX_WIDTH, Math.round(width)));
}

const googleButtonSx = {
  width: '100%',
  height: '40px',
  textTransform: 'none',
  color: PRIMARY_COLOR,
  borderColor: PRIMARY_COLOR,
  backgroundColor: '#fff',
  fontWeight: 'bold',
  fontSize: '16px',
  ':hover': {
    borderColor: PRIMARY_COLOR,
    backgroundColor: '#fff',
  },
} as const;

function GoogleIcon() {
  return (
    <svg width="22" height="22" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ marginRight: '6px' }}>
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

function loadGsiScript(onLoad: () => void): (() => void) | void {
  const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);

  if (existingScript) {
    if (window.google?.accounts?.id) {
      onLoad();
    } else {
      existingScript.addEventListener('load', onLoad);
      return () => existingScript.removeEventListener('load', onLoad);
    }
    return;
  }

  const script = document.createElement('script');
  script.src = GIS_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  script.onload = onLoad;
  document.body.appendChild(script);
}

export type GoogleSignInButtonProps = ButtonProps;

/**
 * Shows Google's personalized "Sign in as ..." button when a browser session exists.
 * Clicks are handled by the invisible overlay so only the parent `onClick` runs.
 * Width always tracks the parent (same as email/password fields).
 */
export function GoogleSignInButton({
  sx,
  startIcon = <GoogleIcon />,
  children = 'Sign In with Google',
  variant = 'outlined',
  type = 'button',
  onClick,
  ...props
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gisContainerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const [gisReady, setGisReady] = useState(false);

  const gisWidth = clampGisWidth(measuredWidth);
  const scaleX = gisWidth > 0 && measuredWidth > 0 ? measuredWidth / gisWidth : 1;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const updateWidth = () => {
      const nextWidth = Math.round(container.offsetWidth);
      setMeasuredWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || gisWidth <= 0) {
      return;
    }

    const renderGoogleButton = () => {
      const gisContainer = gisContainerRef.current;
      const gsi = window.google?.accounts?.id;

      if (!gsi || !gisContainer) {
        return;
      }

      gsi.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: () => {},
        use_fedcm_for_button: true,
      });

      gisContainer.innerHTML = '';
      gsi.renderButton(gisContainer, {
        theme: 'outline',
        size: 'large',
        width: gisWidth,
        text: 'signin_with',
      });

      setGisReady(true);
    };

    setGisReady(false);
    return loadGsiScript(renderGoogleButton) ?? undefined;
  }, [gisWidth]);

  return (
    <Box
      ref={containerRef}
      id="googleSignInButton"
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: '40px',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        ref={gisContainerRef}
        aria-hidden={!gisReady}
        sx={{
          width: gisWidth > 0 ? `${gisWidth}px` : '100%',
          height: '40px',
          opacity: gisReady ? 1 : 0,
          pointerEvents: 'none',
          transformOrigin: 'left center',
          transform: scaleX === 1 ? 'none' : `scaleX(${scaleX})`,
          '& > div': {
            width: '100%',
          },
          '& iframe': {
            display: 'block',
          },
        }}
      />

      {!gisReady ? (
        <Button
          variant={variant}
          type={type}
          startIcon={startIcon}
          onClick={onClick}
          sx={{
            ...googleButtonSx,
            position: 'absolute',
            inset: 0,
          }}
          {...props}
        >
          {children}
        </Button>
      ) : (
        <Button
          variant={variant}
          type={type}
          onClick={onClick}
          aria-label="Sign in with Google"
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            width: '100%',
            minHeight: '40px',
          }}
        />
      )}
    </Box>
  );
}
